import React, { useEffect, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";

export default function PolicyPanel({
  title,
  items: initialItems,
  lastUpdated,
  editable = false,
  onSave,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [items, setItems] = useState([]);

  // Sync state with prop updates
  useEffect(() => {
    setItems(initialItems || []);
  }, [initialItems]);

  const handleAdd = () => {
    setItems((prev) => [
      ...prev,
      { title: "New Item Title", body: "Description of the policy item." },
    ]);
  };

  const handleDelete = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleChange = (index, field, val) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: val };
      return updated;
    });
  };

  const handleMove = (index, direction) => {
    const nextIndex = direction === "up" ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= items.length) return;

    setItems((prev) => {
      const updated = [...prev];
      const temp = updated[index];
      updated[index] = updated[nextIndex];
      updated[nextIndex] = temp;
      return updated;
    });
  };

  const handleSaveClick = async () => {
    if (onSave) {
      const success = await onSave(items);
      if (success !== false) {
        setIsEditing(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleCancelClick = () => {
    setItems(initialItems || []);
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not updated yet";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="rounded-2xl border border-[#EEF0F3] bg-white p-5 sm:p-6 shadow-sm space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between border-b border-[#EEF0F3] pb-5 gap-4">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold text-[#6b0c44]">{title}</h2>
          <p className="text-base font-medium text-[#667085]">
            Last update: {formatDate(lastUpdated)}
          </p>
        </div>

        {editable && (
          <div className="flex items-center gap-3">
            {!isEditing ? (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded-xl bg-[#61013B] px-5 py-2.5 text-base font-semibold text-white transition hover:bg-[#4d0130]"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleCancelClick}
                  className="rounded-xl border border-[#CEB0C2] bg-white px-5 py-2.5 text-base font-semibold text-[#344054] transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveClick}
                  className="rounded-xl bg-[#61013B] px-5 py-2.5 text-base font-semibold text-white transition hover:bg-[#4d0130]"
                >
                  Save
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      {!isEditing ? (
        <ol className="list-decimal space-y-4 pl-5 text-base font-medium leading-7 text-[#475467]">
          {items.map((item, idx) => (
            <li key={idx} className="pl-1">
              <span className="font-semibold text-[#344054]">{item.title}</span>{" "}
              {item.body}
            </li>
          ))}
          {items.length === 0 && (
            <p className="text-gray-500 italic pl-1">No items defined.</p>
          )}
        </ol>
      ) : (
        <div className="space-y-6">
          {items.map((item, index) => (
            <div
              key={index}
              className="rounded-xl border border-[#EEF0F3] p-4 bg-gray-50/50 space-y-4"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-base font-bold text-[#6b0c44]">
                  Item #{index + 1}
                </span>

                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleMove(index, "up")}
                    disabled={index === 0}
                    className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    title="Move Up"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMove(index, "down")}
                    disabled={index === items.length - 1}
                    className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    title="Move Down"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(index)}
                    className="p-1.5 text-red-500 hover:text-red-700 ml-2"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => handleChange(index, "title", e.target.value)}
                    className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#61013B]"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-gray-700">
                    Body
                  </label>
                  <textarea
                    value={item.body}
                    onChange={(e) => handleChange(index, "body", e.target.value)}
                    className="w-full rounded-lg border border-[#D0D5DD] bg-white px-3 py-2 text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#61013B] h-24 resize-y"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#61013B] hover:text-[#4d0130] transition py-1"
          >
            <Plus className="h-4 w-4" /> Add Item
          </button>

          <div className="flex justify-end border-t border-[#EEF0F3] pt-5">
            <button
              type="button"
              onClick={handleSaveClick}
              className="rounded-xl bg-[#61013B] px-6 py-3 text-base font-semibold text-white transition hover:bg-[#4d0130] shadow-sm"
            >
              Save Changes
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
